import { usePrevious } from '@sequencehq/utils'
import { useSequenceInfiniteQuery } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useNotifications } from '@sequencehq/core-components'
import { useEffect, useMemo } from 'react'
import { mapExecutionStatusToLegacyStatuses } from '../legacyWorkflows/legacyWorkflow.adapters'
import { Dashboardv99990101Api } from '@sequencehq/api/dashboard/v99990101'

type WorkflowStatus = Dashboardv99990101Api.Watchtower.WorkflowStatus

type LegacyWorkflowFilters = {
  status: string[]
  trigger: string[]
}

export const useLegacyWorkflows = ({
  filters,
  enabled = true
}: {
  filters: LegacyWorkflowFilters
  enabled?: boolean
}) => {
  const { displayNotification } = useNotifications()

  // If a non-CONTRACT_UPLOADED trigger filter is active, disable the query
  // since all legacy workflows are CONTRACT_EXTRACTION type
  const hasMismatchedTriggerFilter =
    filters.trigger.length > 0 && !filters.trigger.includes('CONTRACT_UPLOADED')

  const legacyStatuses: WorkflowStatus[] = useMemo(() => {
    if (filters.status.length === 0) return []
    return filters.status.flatMap(mapExecutionStatusToLegacyStatuses)
  }, [filters.status])

  const infiniteQuery = useSequenceInfiniteQuery(
    dashboardv99990101Client.getWorkflows,
    {
      limit: 40,
      statuses: legacyStatuses.length > 0 ? legacyStatuses : undefined,
      sortOrder: 'DESC'
    },
    {
      retry: false,
      enabled: enabled && !hasMismatchedTriggerFilter
    }
  )

  const prevErrorState = usePrevious(infiniteQuery.infiniteQuery.isError)

  useEffect(() => {
    if (infiniteQuery.infiniteQuery.isError && !prevErrorState) {
      displayNotification('We could not load legacy workflows', {
        type: 'error'
      })
    }
  }, [prevErrorState, infiniteQuery.infiniteQuery.isError, displayNotification])

  const allWorkflows = useMemo(() => {
    return (
      infiniteQuery.infiniteQuery.data?.pages.flatMap(page => page.items) ?? []
    )
  }, [infiniteQuery.infiniteQuery.data])

  return {
    isPending:
      enabled &&
      !hasMismatchedTriggerFilter &&
      infiniteQuery.infiniteQuery.isPending,
    isFetchingNextPage: infiniteQuery.infiniteQuery.isFetchingNextPage,
    workflows: allWorkflows,
    hasNextPage: infiniteQuery.infiniteQuery.hasNextPage,
    fetchNextPage: infiniteQuery.infiniteQuery.fetchNextPage,
    hasItems: hasMismatchedTriggerFilter ? false : allWorkflows.length > 0
  }
}
